<template>
  <b-card no-body>
    <b-card-header>
      <h4 class="mb-0">
        {{ title }}
      </h4>
    </b-card-header>

    <b-card-body class="pb-0">
      <b-list-group flush>
        <b-list-group-item
          v-for="item in localItems"
          :key="item.id"
          :class="{ 'required-item': item.mandatory, 'locked-item': isItemLocked(item) }"
        >
          <div
            v-if="isItemLocked(item)"
            class="promotion-item promotion-item--locked"
          >
            <div>
              <strong class="text-muted">{{ item.description }}{{ item.mandatory ? "*" : "" }}</strong>
            </div>
            <div class="input-wrapper d-flex justify-content-end">
              <small class="text-muted"><b-icon icon="lock-fill" /> Aguardando assinatura do NDA</small>
            </div>
          </div>

          <div
            v-else
            class="promotion-item"
            :class="{ 'promotion-item--stacked': item.type === 'signer_info' }"
          >
            <div>
              <strong
                :class="
                  item.finished_at &&
                    (item.type === 'document' || item.type === 'contract')
                    ? 'text-success'
                    : ''
                "
              >
                {{ item.description }}{{ item.mandatory ? "*" : "" }}
              </strong>

              <div v-if="item.signatures && item.signatures.length > 0">
                <div
                  v-for="(signature, index) in item.signatures"
                  :key="index"
                >
                  {{ signature.signer_email }} -
                  <small
                    v-if="signature.signed_at"
                    class="text-success"
                  >
                    Assinado em {{ formatDate(signature.signed_at) }}
                  </small>
                  <small v-else>Pendente</small>
                </div>
              </div>
            </div>

            <div class="input-wrapper">
              <!-- signer → email -->
              <b-form-group v-if="item.type === 'signer'">
                <b-form-input
                  v-model.lazy="item.value"
                  type="email"
                  placeholder="Digite o e-mail"
                  :class="{
                    'is-invalid':
                      hasText(item.value) && validateEmail(item.value) === false,
                  }"
                />
              </b-form-group>

              <template v-else-if="item.type === 'signer_info'">
                <!-- signer_info → dedicated guarantor form -->
                <PromotionSignerInfoForm
                  class="guarantor-form"
                  :item-id="item.id"
                  :value="item.value"
                  :marital-status-options="maritalStatusOptions"
                  :address-states="addressStates"
                  :loading-address-states="loadingAddressStates"
                  :city-options="getCityOptions(item.value)"
                  :city-loading="isCityLoading(item.value.address_state_id)"
                  @input="updateSignerInfoValue(item, $event)"
                  @state-change="addressStateChanged(item, $event)"
                  @city-change="addressCityChanged(item, $event)"
                />
              </template>

              <!-- consultant → select -->
              <b-form-group v-else-if="item.type === 'consultant'">
                <v-select
                  v-model="item.value"
                  required
                  :reduce="(e) => e.id"
                  :options="consultantOptions"
                  label="name"
                />
              </b-form-group>

              <!-- financial_solution_value → money -->
              <b-form-group v-else-if="item.type === 'financial_solution_value'">
                <b-form-input
                  v-model="item.value"
                  v-money="moneyFormat"
                  placeholder="Digite o valor"
                />
              </b-form-group>

              <!-- document / contract statuses unchanged -->
              <div
                v-else-if="item.type === 'document'"
                class="d-flex justify-content-end"
              >
                <span
                  v-if="item.finished_at"
                  class="text-success"
                >
                  Assinado ✔
                </span>
                <small v-else>Aguardando assinatura</small>
              </div>

              <div
                v-else-if="item.type === 'financial_solution'"
                class="d-flex justify-content-end"
              >
                <span
                  v-if="item.finished_at"
                  class="text-success"
                >Pago ✔</span>
                <small v-else>Aguardando pagamento</small>
              </div>

              <div v-else-if="item.type === 'contract'">
                <span
                  v-if="item.finished_at"
                  class="text-success"
                >Pago ✔</span>
                <small v-else>Aguardando pagamento</small>
              </div>

              <div v-else>
                Tipo desconhecido: {{ item.type }}
              </div>
            </div>
          </div>
        </b-list-group-item>
      </b-list-group>
    </b-card-body>

    <b-card-footer
      class="d-flex flex-column align-items-stretch"
    >
      <b-alert
        v-if="hasDuplicateSignerEmails"
        variant="danger"
        show
        class="mb-1 small"
      >
        O fiador e as testemunhas precisam ser pessoas diferentes. Verifique os
        e-mails informados antes de continuar.
      </b-alert>

      <div
        class="d-flex align-items-center"
        :class="allowContinue ? 'justify-content-between' : 'justify-content-end'"
      >
      <b-button
        v-if="allowContinue"
        variant="primary"
        :disabled="loading"
        @click="submit"
      >
        <b-spinner
          v-if="loading"
          small
          class="mr-1"
        />
        <span>
          {{
            loading
              ? "Aguarde... esta operação pode levar alguns minutos"
              : buttonText
          }}
        </span>
      </b-button>

      <small class="text-muted">Preenchimento obrigatório (*)</small>
      </div>
    </b-card-footer>
  </b-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as types from '@/modules/promotion/store/types'
import * as sharedTypes from '@/modules/shared/store/types'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import useVuelidate from '@vuelidate/core'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getBRLFormat } from '@/helpers/formatting'
import { VMoney } from 'v-money'
import maritalStatuses from '@/constants/marital_statuses_types'
import cpfIsValid from '@/validators/cpf'
import rgIsValid from '@/validators/rg'
import PromotionSignerInfoForm from './PromotionSignerInfoForm.vue'

export default {
  name: 'PromotionStep',
  components: {
    vSelect,
    PromotionSignerInfoForm,
  },
  directives: {
    Ripple,
    money: VMoney,
  },
  props: {
    items: { type: Array, required: true },
    title: { type: String, default: 'Passo' },
    subtitle: { type: String, default: '' },
    buttonText: { type: String, default: 'Continuar' },
    consultantOptions: { type: Array, default: () => [] },
    allowContinue: { type: Boolean, default: true },
  },
  data() {
    return {
      localItems: [],
      loading: false,
      loadingAddressStates: false,
      loadingCitiesByState: {},
      addressCitiesByState: {},
    }
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() }
  },
  computed: {
    ...mapGetters({
      addressStates: sharedTypes.ADDRESS_STATES,
    }),
    hasUnfinishedFirstOrderItems() {
      return this.localItems.some(
        item => (item.item_order ?? 1) === 1 && item.type === 'document' && !item.finished_at
      )
    },
    hasDuplicateSignerEmails() {
      const emails = this.localItems
        .filter(item => item.type === 'signer' || item.type === 'signer_info')
        .map(item => {
          const raw = item.type === 'signer_info' ? item.value.email : item.value
          return (raw || '').trim().toLowerCase()
        })
        .filter(email => email && this.validateEmail(email) !== false)

      return emails.length !== new Set(emails).size
    },
    moneyFormat() {
      return {
        ...getBRLFormat(),
        masked: false,
      }
    },
    maritalStatusOptions() {
      return [
        { value: '', text: 'Selecione o estado civil' },
        ...maritalStatuses.map(status => ({
          value: status.label,
          text: status.label,
        })),
      ]
    },
  },
  watch: {
    items: {
      deep: true,
      immediate: true,
      handler(newItems) {
        this.localItems = newItems.map(item => {
          let value = null

          if (item.type === 'signer_info') {
            value = {
              email: item.signer_email || '',
              ...this.normalizeSignerInfo(item.signer_info),
            }
          } else if (item.type === 'signer') {
            value = item.signer_email
          } else if (item.type === 'consultant') {
            value = item.consultant_lead_owner_id ?? item.consultant_seller_id
          } else if (item.type === 'financial_solution_value') {
            value = item.financial_solution_value
          }

          return { ...item, value, signatures: item.signatures || [] }
        })

        this.syncAllSignerInfoLocations()
      },
    },
  },
  mounted() {
    this.fetchAddressStates()
  },
  methods: {
    ...mapActions({
      saveItem: types.SAVE_CHECKLIST_ITEM,
      getAddressStates: sharedTypes.GET_ADDRESS_STATES,
      getAddressCities: sharedTypes.GET_ADDRESS_CITIES,
    }),

    isItemLocked(item) {
      return (item.item_order ?? 1) > 1 && this.hasUnfinishedFirstOrderItems
    },

    formatDate(dateString) {
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }

      return new Date(dateString)
        .toLocaleDateString('pt-BR', options)
        .replace(',', ' às')
    },

    validateEmail(email) {
      if (!email) return null
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },

    hasText(value) {
      return String(value || '').trim() !== ''
    },

    async fetchAddressStates() {
      this.loadingAddressStates = true

      try {
        await this.getAddressStates()
        this.syncAllSignerInfoLocations()
      } catch (err) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os estados. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loadingAddressStates = false
      }
    },

    normalizeText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
    },

    updateSignerInfoValue(item, nextValue) {
      this.$set(item, 'value', {
        ...item.value,
        ...nextValue,
      })
    },

    getSelectedState(stateId) {
      return this.addressStates.find(
        state => String(state.id) === String(stateId),
      )
    },

    getSelectedCity(stateId, cityId) {
      const cities = this.addressCitiesByState[stateId] || []
      return cities.find(city => String(city.id) === String(cityId))
    },

    findStateByValue(stateValue) {
      if (!this.hasText(stateValue)) {
        return undefined
      }

      const normalizedState = this.normalizeText(stateValue)
      return this.addressStates.find(state => (
        this.normalizeText(state.name) === normalizedState
          || this.normalizeText(state.uf) === normalizedState
      ))
    },

    async ensureCitiesLoaded(stateId) {
      if (!stateId) {
        return []
      }

      if (this.addressCitiesByState[stateId]) {
        return this.addressCitiesByState[stateId]
      }

      this.$set(this.loadingCitiesByState, stateId, true)

      try {
        const resp = await this.getAddressCities({ addressState: stateId })
        const cities = Array.isArray(resp.data) ? resp.data : []
        this.$set(this.addressCitiesByState, stateId, cities)
        return cities
      } catch (err) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar as cidades. Entre em contato com o setor de TI.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return []
      } finally {
        this.$set(this.loadingCitiesByState, stateId, false)
      }
    },

    isCityLoading(stateId) {
      return !!this.loadingCitiesByState[stateId]
    },

    getCityOptions(itemValue) {
      return this.addressCitiesByState[itemValue?.address_state_id] || []
    },

    async syncSignerInfoLocation(item) {
      if (!item?.value || !this.addressStates.length) {
        return
      }

      const currentState = this.getSelectedState(item.value.address_state_id)
        || this.findStateByValue(item.value.state)

      if (!currentState) {
        return
      }

      this.updateSignerInfoValue(item, {
        ...item.value,
        address_state_id: currentState.id,
        state: currentState.uf || currentState.name || '',
      })

      const cities = await this.ensureCitiesLoaded(currentState.id)
      const currentCity = cities.find(city => (
        String(city.id) === String(item.value.address_city_id)
          || this.normalizeText(city.name) === this.normalizeText(item.value.city)
      ))

      if (currentCity) {
        this.updateSignerInfoValue(item, {
          ...item.value,
          address_city_id: currentCity.id,
          city: currentCity.name,
        })
      }
    },

    syncAllSignerInfoLocations() {
      this.localItems.forEach(item => {
        if (item.type === 'signer_info') {
          this.syncSignerInfoLocation(item)
        }
      })
    },

    async addressStateChanged(item, nextValue) {
      this.updateSignerInfoValue(item, nextValue)

      const state = this.getSelectedState(nextValue.address_state_id)

      this.updateSignerInfoValue(item, {
        ...item.value,
        address_state_id: nextValue.address_state_id,
        address_city_id: undefined,
        state: state?.uf || '',
        city: '',
      })

      if (nextValue.address_state_id) {
        await this.ensureCitiesLoaded(nextValue.address_state_id)
      }
    },

    addressCityChanged(item, nextValue) {
      this.updateSignerInfoValue(item, nextValue)

      const city = this.getSelectedCity(
        nextValue.address_state_id,
        nextValue.address_city_id,
      )

      this.updateSignerInfoValue(item, {
        ...item.value,
        address_city_id: nextValue.address_city_id,
        city: city?.name || '',
      })
    },

    getEmptySignerInfo() {
      return {
        full_name: '',
        nationality: '',
        marital_status: '',
        profession: '',
        rg: '',
        issuing_agency: '',
        cpf: '',
        address: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        address_state_id: undefined,
        address_city_id: undefined,
        cep: '',
      }
    },

    normalizeSignerInfo(value) {
      const emptySignerInfo = this.getEmptySignerInfo()

      if (!value) {
        return emptySignerInfo
      }

      let parsedValue = value

      if (typeof value === 'string') {
        try {
          parsedValue = JSON.parse(value)
        } catch (error) {
          parsedValue = this.parseLegacySignerInfo(value)
        }
      }

      if (
        !parsedValue
        || Array.isArray(parsedValue)
        || typeof parsedValue !== 'object'
      ) {
        return emptySignerInfo
      }

      return {
        ...emptySignerInfo,
        full_name: parsedValue.full_name || '',
        nationality: parsedValue.nationality || '',
        marital_status: parsedValue.marital_status || '',
        profession: parsedValue.profession || '',
        rg: parsedValue.rg || '',
        issuing_agency: parsedValue.issuing_agency || parsedValue.rg_issuer || '',
        cpf: parsedValue.cpf || '',
        address: parsedValue.address || '',
        number: parsedValue.number || '',
        complement: parsedValue.complement || '',
        neighborhood: parsedValue.neighborhood || '',
        city: parsedValue.city || parsedValue.municipality || '',
        state: parsedValue.state || '',
        address_state_id:
          parsedValue.address_state_id || parsedValue.state_id || undefined,
        address_city_id:
          parsedValue.address_city_id || parsedValue.city_id || undefined,
        cep: parsedValue.cep || '',
      }
    },

    parseLegacySignerInfo(value) {
      const legacyPattern = /^(.*?),\s*(.*?),\s*(.*?),\s*(.*?),\s*portador\(a\) da Cédula de Identidade RG nº\s*(.*?),\s*expedido por\s*(.*?),\s*e inscrito\(a\) no CPF sob nº\s*(.*?),\s*residente e domiciliado\(a\) na Rua\s*(.*?),\s*nº\s*(.*?),\s*bairro\s*(.*?),\s*no município de\s*(.*?),\s*estado de\s*(.*?),\s*CEP\s*(.*?)$/i
      const matches = String(value || '').trim().match(legacyPattern)

      if (!matches) {
        return this.getEmptySignerInfo()
      }

      return {
        full_name: matches[1] || '',
        nationality: matches[2] || '',
        marital_status: matches[3] || '',
        profession: matches[4] || '',
        rg: matches[5] || '',
        issuing_agency: matches[6] || '',
        cpf: matches[7] || '',
        address: matches[8] || '',
        number: matches[9] || '',
        complement: '',
        neighborhood: matches[10] || '',
        city: matches[11] || '',
        state: matches[12] || '',
        address_state_id: undefined,
        address_city_id: undefined,
        cep: matches[13] || '',
      }
    },

    getSerializedSignerInfo(value) {
      const selectedState = this.getSelectedState(value.address_state_id)
      const selectedCity = this.getSelectedCity(
        value.address_state_id,
        value.address_city_id,
      )

      return {
        full_name: String(value.full_name || '').trim(),
        nationality: String(value.nationality || '').trim(),
        marital_status: String(value.marital_status || '').trim(),
        profession: String(value.profession || '').trim(),
        rg: String(value.rg || '').trim(),
        issuing_agency: String(value.issuing_agency || '').trim(),
        cpf: String(value.cpf || '').trim(),
        address: String(value.address || '').trim(),
        number: String(value.number || '').trim(),
        complement: String(value.complement || '').trim(),
        neighborhood: String(value.neighborhood || '').trim(),
        city: String(selectedCity?.name || value.city || '').trim(),
        state: String(selectedState?.uf || value.state || '').trim(),
        state_name: String(selectedState?.name || '').trim(),
        address_state_id: value.address_state_id || null,
        address_city_id: value.address_city_id || null,
        cep: String(value.cep || '').trim(),
      }
    },

    isSignerInfoComplete(value) {
      if (!value) {
        return false
      }

      const requiredFields = [
        'full_name',
        'nationality',
        'marital_status',
        'profession',
        'rg',
        'issuing_agency',
        'cpf',
        'address',
        'number',
        'neighborhood',
        'city',
        'state',
        'cep',
      ]

      return (
        requiredFields.every(field => this.hasText(value[field]))
        && cpfIsValid(value.cpf)
        && rgIsValid(value.rg)
        && !!value.address_state_id
        && !!value.address_city_id
      )
    },

    async submit() {
      this.loading = true

      try {
        const itemsToUpdate = this.localItems.map(item => {
          const payload = {
            item_id: item.id,
            document_signature_id: item.document_signature_id,
          }

          switch (item.type) {
            case 'signer_info':
              payload.signer_email = item.value.email
              payload.signer_info = this.getSerializedSignerInfo(item.value)
              break
            case 'signer':
              payload.value = item.value
              payload.signer_email = item.value
              break
            case 'consultant':
              payload.value = item.value
              payload.consultant_id = item.value
              break
            case 'financial_solution_value': {
              const raw = String(item.value)
              payload.value = parseFloat(
                raw.replace(/R\$\s?/g, '').replace(/\./g, '').replace(/,/g, '.'),
              )
              break
            }
            default:
              break
          }

          return payload
        })

        await this.saveItem(itemsToUpdate)
        this.$emit('next')
      } catch (err) {
        const message = err.response?.data?.error
          ? err.response.data.error
          : 'Erro ao avançar no fluxo de promoção. Entre em contato com o setor de TI'

        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// .required-item {
//   border-left: 3px solid #e55353 !important;
// }
.input-wrapper {
  min-width: 200px;

  input {
    width: 220px;
  }

  textarea {
    display: block;
    width: 100%;
    box-sizing: border-box;
    resize: vertical;
    margin-top: 0.5rem;
    min-width: 350px;
  }
}

.promotion-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.promotion-item--stacked {
  display: block;
}

.promotion-item--stacked > div:first-child {
  margin-bottom: 0.75rem;
}

.promotion-item--stacked .input-wrapper {
  width: 100%;
}

.guarantor-form {
  width: 100%;
}

.locked-item {
  opacity: 0.55;
  pointer-events: none;
}

.promotion-item--locked {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

::v-deep .vs__selected-options {
  input {
    width: 186px !important;
  }
}

.list-group-item {
  border: none;
  background-color: transparent !important;
  color: inherit !important;

  &:hover {
    background: none !important;
  }

  &:focus,
  &:active,
  &.active {
    background-color: transparent !important;
    color: inherit !important;
    box-shadow: none !important;
  }
}

.card-header {
  border-bottom: 1px #3b4253 solid;
}
</style>
